import React, {useCallback, useMemo, useState} from 'react';
import {renderComponent} from '../../dyanimcFormUtil.tsx';
import {FormComponentProps, PanelComponent} from "../../DynamicFormTypes.ts";
import {PanelStaticHeaderWatched} from "./PanelStaticHeaderWatched.tsx";
import {MoreHorizontal} from "lucide-react";


const PanelRenderer: React.FC<FormComponentProps<PanelComponent>> = ({ component }) => {

    const [showAllHeaders, setShowAllHeaders] = useState(false);

    const toggleShowAllHeaders = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowAllHeaders((s) => !s);
    }, []);

    // memoize static headers list
    const staticHeadersEl = useMemo(() => {
        const list = component.staticHeaders || [];
        if (!list.length) return null;
        const display = showAllHeaders ? list : list.slice(0, 3);
        const hasMore = !showAllHeaders && list.length > 3;

        return (
            <div className="flex items-center justify-end ml-4 space-x-3 text-sm text-gray-500 overflow-hidden">
                {display.map((h) => (
                    <PanelStaticHeaderWatched key={`static-header-${h.fieldKey}`} staticHeader={h} />
                ))}

                {hasMore && (
                    <button type="button" className="text-indigo-600 hover:text-indigo-800 flex items-center"
                            onClick={toggleShowAllHeaders} aria-label="Show more headers">
                        <MoreHorizontal className="h-4 w-4 mr-1" />
                        <span>+{list.length - 3} more</span>
                    </button>
                )}

                {showAllHeaders && (
                    <button type="button" className="text-indigo-600 hover:text-indigo-800"
                            onClick={toggleShowAllHeaders} aria-label="Show fewer headers">
                        Show fewer
                    </button>
                )}
            </div>
        );
    }, [component.staticHeaders, showAllHeaders, toggleShowAllHeaders]);

    return (
        <div
            className={`mb-6 border border-gray-200 rounded-md w-full shadow-sm overflow-visible`}
            id={component.fieldKey}
        >
            {/* Panel Header */}
            {component.showLabel && (
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center w-full">
                        <h3 className="text-lg font-medium text-gray-900">
                            {component.label}
                        </h3>

                        <div className="ml-auto overflow-x-auto pb-1 flex items-center">
                            {staticHeadersEl}
                        </div>
                    </div>
                </div>
            )}

            {/* Container for nested components */}
            <div
                className={"max-h-none opacity-100 overflow-visible"}
            >
                {component.components && (
                    <div className="p-4 w-full">
                        {component.components.map((nestedComponent, index) =>
                            <React.Fragment key={`panel-item-${index}-${nestedComponent.fieldKey}`}>
                                {renderComponent({
                                    component: nestedComponent,
                                })}
                            </React.Fragment>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export const Panel = React.memo(PanelRenderer);
